import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "components/primitives/Grid";
import moment from "moment";
import { Measurement, Metadata, MetadataType } from "types";
import * as Components from "./ActivityCardTable.components";
import MetadataHelpers from "helpers/MetadataHelpers";
import useUnitFormatter from "hooks/useUnitFormatter";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";

interface ActivityCardTableProps {
  measurements: Measurement[];
  metadata: Metadata;
}

function ActivityCardTable(props: ActivityCardTableProps) {
  const { measurements, metadata } = props;
  const formatUnit = useUnitFormatter();
  const { t } = useTranslation();

  // Circuits resolve their default metric (e.g. "Rounds Completed", "Total Distance") through the
  // same shared resolver as exercises; weight/reps/variant are exercise-only and stay guarded below.
  const { exercise, defaultMetricMetadata, weightMetadata } = useMemo(() => {
    const exercise = metadata.type === MetadataType.EXERCISE ? metadata.metadata : null;
    return {
      exercise,
      defaultMetricMetadata: MetadataHelpers.getMetadataMetricByField(metadata, null),
      weightMetadata: exercise?.metrics.find((exerciseMetric) => exerciseMetric.field === "weight"),
    };
  }, [metadata]);

  const showWeight = Boolean(weightMetadata);
  const showReps = Boolean(exercise?.repetitions.length);
  const showVariant = Boolean(exercise?.variants.length);
  const showDefaultMetric = !showWeight;

  const gridRowProps = { size: "grow" } as const;

  return (
    <Box sx={{ pt: 1 }}>
      <Grid container wrap="nowrap">
        <Grid {...gridRowProps} sx={{ width: 48 }}>
          <Components.TableHead>{t("global.time_label_text")}</Components.TableHead>
        </Grid>
        {showReps && (
          <Grid {...gridRowProps}>
            <Components.TableHead>{t("global.reps_label_text")}</Components.TableHead>
          </Grid>
        )}
        {showWeight && (
          <Grid {...gridRowProps}>
            <Components.TableHead>{t("global.weight_label_text")}</Components.TableHead>
          </Grid>
        )}
        {showVariant && (
          <Grid {...gridRowProps}>
            <Components.TableHead>{t("global.variant_label_text")}</Components.TableHead>
          </Grid>
        )}
        {showDefaultMetric && defaultMetricMetadata && (
          <Grid {...gridRowProps}>
            <Components.TableHead title={defaultMetricMetadata.name}>{defaultMetricMetadata.name}</Components.TableHead>
          </Grid>
        )}
      </Grid>
      {measurements.map((measurement, measurementIndex) => {
        const weight = measurement.metrics.find((metric) => metric.field === "weight")?.value;
        const defaultMetricValue = measurement.metrics.find(
          (metric) => metric.field === defaultMetricMetadata?.field
        )?.value;

        return (
          <Fragment key={measurement.id}>
            <Box sx={{ pt: 1, pb: 1 }}>
              <Grid container wrap="nowrap" sx={{ alignContent: "center", justifyContent: "flex-start" }}>
                <Grid {...gridRowProps} sx={{ width: 48 }}>
                  <Components.TableData title={moment(measurement.completedDate).toLocaleString()}>
                    {moment(measurement.completedDate).format("HH:mm")}
                  </Components.TableData>
                </Grid>
                {showReps && (
                  <Grid {...gridRowProps}>
                    <Components.TableData>
                      {measurement.metrics.find((metric) => metric.field === "repCount")?.value}
                    </Components.TableData>
                  </Grid>
                )}
                {showWeight && (
                  <Grid {...gridRowProps}>
                    <Components.TableData>{formatUnit(weight, weightMetadata)}</Components.TableData>
                  </Grid>
                )}
                {showVariant && measurement.variant && (
                  <Grid {...gridRowProps}>
                    <Components.TableData>{measurement.variant.slice(0, 1)}</Components.TableData>
                  </Grid>
                )}
                {showDefaultMetric && defaultMetricMetadata && (
                  <Grid {...gridRowProps}>
                    <Components.TableData
                      title={`${defaultMetricMetadata.name} ${formatUnit(defaultMetricValue, defaultMetricMetadata)}`}
                    >
                      {formatUnit(defaultMetricValue, defaultMetricMetadata)}
                    </Components.TableData>
                  </Grid>
                )}
              </Grid>
            </Box>
            {measurementIndex < measurements.length - 1 && <Divider />}
          </Fragment>
        );
      })}
    </Box>
  );
}

export default ActivityCardTable;
